import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  ButtonBase,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkunreadOutlinedIcon from "@mui/icons-material/MarkunreadOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Divider from "@mui/material/Divider";
import ChatPage from "./Chats";

const Messages = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData } = useAuth();
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [formData, setFormData] = useState({
    sender: userId,
    receiver: "",
    message: "",
    message_type: "text",
  });
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [openStaffDialog, setOpenStaffDialog] = useState(false);

  useEffect(() => {
    async function fetchMessages() {
      try {
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
        } else {
          toast.error(chatsResponse.message || "Failed to load messages");
        }

        const staffsResponse = await getData("proccess.php", "staffs");
        if (staffsResponse.success) {
          setStaffs(staffsResponse.data);
        } else {
          toast.error(staffsResponse.message || "Failed to load messages");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch messages");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMessages();
    const intervalId = setInterval(fetchMessages, 1000);
    return () => clearInterval(intervalId);
  }, [userId, getData]);

  const getUserNames = (userId) => {
    const staff = staffs.find((s) => String(s.Id) === String(userId));
    return staff ? `${staff.FirstName}` : "Unknown";
  };

  const setChat = async (chatID) => {
    try {
      setSelectedChatId(chatID);
      const response = await getData("proccess.php", "messages_V2", chatID);
      if (response.success) {
        setMessages(response.data);

        const messagesToUpdate = response.data.filter(
          (msg) => msg.status === 0
        );
        for (const message of messagesToUpdate) {
          if (message.status === 0 && message.receiver === userId) {
            try {
              const updatePayload = {
                convId: message.convId,
                sender: message.sender,
                receiver: message.receiver,
                message: message.message,
                status: 1,
              };
              const updateResponse = await updateData(
                "proccess.php",
                "messages_V2",
                updatePayload,
                message.id
              );
              if (!updateResponse.success) {
                console.error(
                  `Failed to update message ${message.id}: ${updateResponse.message}`
                );
              }
            } catch (err) {
              console.error(`Error updating message ${message.id}:`, err);
            }
          }
        }

        setFormData((prev) => ({
          ...prev,
          receiver:
            chats.find((c) => c.id === chatID).sender === userId
              ? chats.find((c) => c.id === chatID).receiver
              : chats.find((c) => c.id === chatID).sender,
        }));
      } else {
        const newChat = chats.find(
            (chat) =>
              (chat.id === chatID)
          );
          if (newChat && newChat.receiver !== userId) {
            setNewChat(newChat.id, newChat.receiver);
          }
      }
    } catch (err) {
      toast.error("Network error: Failed to load chat messages");
      console.error("Fetch error:", err);
    }
  };

  const setNewChat = (chatID, receiverId) => {
    setSelectedChatId(chatID);
    setMessages([]);
    setFormData((prev) => ({
      ...prev,
      receiver: receiverId,
    }));
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      message: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.receiver) {
      toast.error("Please select a chat to send a message");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    try {
      const payload = {
        ...formData,
        convId: selectedChatId,
      };
      const response = await addData("proccess.php", "messages_V2", payload);
      if (response.success) {
        toast.success("Message sent successfully");
        setMessages((prev) => [...prev, payload]);
        setFormData((prev) => ({ ...prev, message: "" }));
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
        }
      } else {
        toast.error(response.message || "Failed to send message");
      }
    } catch (err) {
      toast.error("Network error: Failed to send message");
      console.error("Submit error:", err);
    }
  };

  const handleBackToChats = () => {
    setSelectedChatId(null);
    setMessages([]);
  };

  const handleOpenStaffDialog = () => {
    setOpenStaffDialog(true);
  };

  const handleCloseStaffDialog = () => {
    setOpenStaffDialog(false);
  };

  const handleStaffSelect = async (staffId) => {
    try {
      const chatPayload = {
        sender: userId,
        receiver: staffId,
        message: null,
        status: 0,
      };
      const response = await addData("proccess.php", "chat_V2", chatPayload);
      if (response.success) {
        toast.success("Chat created successfully");
        const chatsResponse = await getData("proccess.php", "chat_V2");
        if (chatsResponse.success) {
          setChats(chatsResponse.data);
          const newChat = chatsResponse.data.find(
            (chat) =>
              (chat.sender === userId && chat.receiver === staffId) ||
              (chat.sender === staffId && chat.receiver === userId)
          );
          if (newChat) {
            setNewChat(newChat.id, staffId);
          }
        }
        handleCloseStaffDialog();
      } else {
        toast.error(response.message || "Failed to create chat");
      }
    } catch (err) {
      toast.error("Network error: Failed to create chat");
      console.error("Create chat error:", err);
    }
  };

  const availableStaff = staffs.filter(
    (staff) =>
      String(staff.Id) !== String(userId) &&
      !chats.some(
        (chat) =>
          (chat.sender === userId && chat.receiver === staff.Id) ||
          (chat.sender === staff.Id && chat.receiver === userId)
      )
  );

  return (
    <Box
      mt={2}
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      height="85vh"
    >
      <Box
        display={{ xs: selectedChatId ? "none" : "block", md: "block" }}
        p="1rem"
        width={{ xs: "100%", md: "350px" }}
        height="85vh"
        sx={{
          backgroundColor: colors.primary[500],
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          paddingBottom="1rem"
        >
          <Typography variant="h4" fontWeight={700} marginBottom={1.5}>
            Messages
          </Typography>
          <IconButton onClick={handleOpenStaffDialog}>
            <AddCommentOutlinedIcon />
          </IconButton>
        </Box>
        <Form>
          <input
            type="search"
            placeholder="Search ..."
            style={{
              padding: ".5rem",
              width: "100%",
              backgroundColor: colors.primary[400],
              border: "none",
              color: "#fff",
            }}
          />
        </Form>
        <Box
          height="70vh"
          marginTop="1rem"
          sx={{ overflowY: "auto", overflowX: "hidden" }}
        >
          {chats.find((m) => m.sender === userId || m.receiver === userId) ? (
            chats.map((chat) => (
              chat.sender === userId ? (
                <Box
                  key={chat.id}
                  borderBottom={`.1rem solid ${colors.primary[400]}`}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: ".3rem",
                      marginTop: ".3rem",
                    }}
                    onClick={() => setChat(chat.id)}
                  >
                    <Avatar
                      alt="Remy Sharp"
                      src={require(`../../assets/profiles/logo-blog.png`)}
                    />
                    <Typography
                      padding="0 .5rem"
                      fontWeight={700}
                      sx={{ width: "100%" }}
                    >
                      {chat.sender === userId
                        ? getUserNames(chat.receiver)
                        : getUserNames(chat.sender)}
                    </Typography>
                    {chat.status === 0 ? (
                      <Box
                        flex={1}
                        maxWidth={200}
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Typography
                          fontWeight={700}
                          padding="0 .5rem"
                          color="#0af1a8ff"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.message}
                        </Typography>
                        <MarkunreadOutlinedIcon
                          sx={{ color: "#2cbf91", margin: "0 .5rem" }}
                        />
                      </Box>
                    ) : (
                      <Box
                        flex={1}
                        maxWidth={200}
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Typography
                          padding="0 .5rem"
                          color="#fcfcfcff"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.message}
                        </Typography>
                        <DraftsOutlinedIcon
                          sx={{ color: "#ffffffff", margin: "0 .5rem" }}
                        />
                      </Box>
                    )}
                  </ButtonBase>
                </Box>
              ): chat.receiver === userId ?(
                <Box
                  key={chat.id}
                  borderBottom={`.1rem solid ${colors.primary[400]}`}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: ".3rem",
                      marginTop: ".3rem",
                    }}
                    onClick={() => setChat(chat.id)}
                  >
                    <Avatar
                      alt="Remy Sharp"
                      src={require(`../../assets/profiles/logo-blog.png`)}
                    />
                    <Typography
                      padding="0 .5rem"
                      fontWeight={700}
                      sx={{ width: "100%" }}
                    >
                      {chat.sender === userId
                        ? getUserNames(chat.receiver)
                        : getUserNames(chat.sender)}
                    </Typography>
                    {chat.status === 0 ? (
                      <Box
                        flex={1}
                        maxWidth={200}
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Typography
                          fontWeight={700}
                          padding="0 .5rem"
                          color="#0af1a8ff"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.message}
                        </Typography>
                        <MarkunreadOutlinedIcon
                          sx={{ color: "#2cbf91", margin: "0 .5rem" }}
                        />
                      </Box>
                    ) : (
                      <Box
                        flex={1}
                        maxWidth={200}
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Typography
                          padding="0 .5rem"
                          color="#fcfcfcff"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.message}
                        </Typography>
                        <DraftsOutlinedIcon
                          sx={{ color: "#ffffffff", margin: "0 .5rem" }}
                        />
                      </Box>
                    )}
                  </ButtonBase>
                </Box>
              ): (<></>)
            ))
          ) : (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <Typography>You do not have any conversation</Typography>
              <IconButton onClick={handleOpenStaffDialog}>
                <AddCommentOutlinedIcon sx={{ fontSize: "2rem" }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        display={{ xs: selectedChatId ? "flex" : "none", md: "flex" }}
        position="relative"
        flexDirection="column"
        alignItems="center"
        p="1rem"
        flex={1}
        minWidth={{ xs: "100%", md: "350px" }}
        height="85vh"
        sx={{
          backgroundColor: colors.primary[600],
        }}
      >
        {selectedChatId ? (
          <>
            <Box
              display={{ xs: "block", md: "none", width: "100%" }}
              position="absolute"
              top="0"
              left="0"
            >
              <IconButton onClick={handleBackToChats} sx={{ padding: ".5rem" }}>
                <ArrowBackIosNewIcon />
              </IconButton>
              <Divider
                sx={{
                  color: colors.primary[300],
                  padding: ".1rem",
                  background: colors.primary[400],
                }}
              />
            </Box>

            <ChatPage userId={userId} params={messages} />
          </>
        ) : (
          <Box
            height="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography>Select a chat to view messages</Typography>
          </Box>
        )}
        <Box
          width="90%"
          sx={{ backgroundColor: colors.primary[500] }}
          minHeight="20px"
          position="absolute"
          bottom="20px"
          borderRadius="1rem"
          padding=".5rem"
        >
          <Form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              alignItems: "end",
              justifyContent: "center",
            }}
          >
            <IconButton>
              <AttachFileOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </IconButton>
            <TextField
              sx={{ width: "80%" }}
              id="standard-multiline-flexible"
              label="Type your message"
              multiline
              maxRows={4}
              variant="standard"
              value={formData.message}
              onChange={handleInputChange}
            />
            <Button type="submit">
              <SendOutlinedIcon />
            </Button>
          </Form>
        </Box>
      </Box>

      <Dialog
        open={openStaffDialog}
        onClose={handleCloseStaffDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select a Staff Member to Start a Chat</DialogTitle>
        <DialogContent>
          {availableStaff.length > 0 ? (
            <List>
              {availableStaff.map((staff) => (
                <ListItem
                  key={staff.Id}
                  button
                  onClick={() => handleStaffSelect(staff.Id)}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={require(`../../assets/profiles/logo-blog.png`)}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={staff.FirstName} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>
              No available staff to start a new chat with.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStaffDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;